<template>
    <button
        :title="title"
        class="tw-relative tw-text-sm tw-font-medium tw-rounded-md tw-border tw-text-center tw-transition-all tw-duration-200 tw-ease-in-out tw-shadow-sm hover:tw-shadow disabled:tw-opacity-50 disabled:tw-cursor-not-allowed disabled:tw-bg-gray-100 disabled:tw-text-gray-400 disabled:tw-border-gray-200 disabled:tw-shadow-none focus:tw-outline-none active:tw-scale-[0.98] active:tw-shadow-inner"
        :class="classes"
        :type="buttonType"
        :disabled="disabled || loading"
    >
        <slot></slot>
        <ContainedLoader v-if="loading" />
    </button>
</template>
<script setup lang="ts">
import ContainedLoader from '@/js/components/controls/ContainedLoader.vue';
import { computed } from 'vue';
import type { PropType } from 'vue';

type ButtonType =
    | 'is-primary'
    | 'is-danger'
    | 'is-success'
    | 'is-info'
    | 'is-warning'
    | 'is-secondary'
    | 'is-ghost'
    | 'is-xero'
    | 'is-link'
    | 'is-submenu'
    | 'is-dropdown';

const props = defineProps({
    disabled: {
        type: Boolean,
        default: false,
    },
    loading: {
        type: Boolean,
        default: false,
    },
    active: {
        type: Boolean,
        default: false,
    },
    type: {
        type: String as PropType<ButtonType | null>,
        required: false,
        validator: (value: ButtonType | null) => {
            return value
                ? [
                      'is-primary',
                      'is-danger',
                      'is-success',
                      'is-info',
                      'is-warning',
                      'is-secondary',
                      'is-xero',
                      'is-link',
                      'is-ghost',
                      'is-submenu',
                      'is-dropdown',
                  ].includes(value)
                : true;
        },
        default: null,
    },
    compact: {
        type: Boolean,
        default: false,
    },
    buttonType: {
        type: String as PropType<'button' | 'submit' | 'reset'>,
        default: 'button',
        validator: (value: string) => {
            return ['button', 'submit', 'reset'].includes(value);
        },
    },
    title: {
        type: String,
        default: null,
    },
    square: {
        type: Boolean,
        default: false,
    },
    glass: {
        type: Boolean,
        default: false,
    },
});

const buttonStyles: Record<
    ButtonType | 'default',
    { default: string; glass?: string; active?: string; glassActive?: string }
> = {
    'is-primary': {
        default:
            'tw-bg-eventwise-blue tw-text-white tw-border-eventwise-blue hover:tw-bg-eventwise-blue/90 hover:tw-border-eventwise-blue/90 active:tw-bg-eventwise-blue/80',
        glass: 'tw-bg-eventwise-blue/10 tw-text-eventwise-blue tw-border-eventwise-blue/40 hover:tw-bg-eventwise-blue hover:tw-text-white hover:tw-border-eventwise-blue active:tw-bg-eventwise-blue/90',
        active: 'tw-bg-eventwise-blue/90 tw-text-white tw-border-eventwise-blue/90 tw-shadow active:tw-bg-eventwise-blue/80',
        glassActive:
            'tw-bg-eventwise-blue tw-text-white tw-border-eventwise-blue tw-shadow active:tw-bg-eventwise-blue/90',
    },
    'is-danger': {
        default:
            'tw-bg-white tw-text-red-600 tw-border-red-300 hover:tw-bg-red-50 hover:tw-border-red-400 active:tw-bg-red-100',
        glass: 'tw-bg-red-400/10 tw-text-red-400 tw-border-red-400/40 hover:tw-bg-red-500 hover:tw-text-white hover:tw-border-red-500 active:tw-bg-red-600',
        active: 'tw-bg-red-50 tw-text-red-600 tw-border-red-400 tw-shadow active:tw-bg-red-100',
        glassActive: 'tw-bg-red-500 tw-text-white tw-border-red-500 tw-shadow active:tw-bg-red-600',
    },
    'is-success': {
        default:
            'tw-bg-eventwise-green tw-text-white tw-border-eventwise-green hover:tw-bg-eventwise-green/90 hover:tw-border-eventwise-green/90 active:tw-bg-eventwise-green/80',
        glass: 'tw-bg-eventwise-green/10 tw-text-eventwise-green tw-border-eventwise-green/40 hover:tw-bg-eventwise-green hover:tw-text-white hover:tw-border-eventwise-green active:tw-bg-eventwise-green/90',
        active: 'tw-bg-eventwise-green/90 tw-text-white tw-border-eventwise-green/90 tw-shadow active:tw-bg-eventwise-green/80',
        glassActive:
            'tw-bg-eventwise-green tw-text-white tw-border-eventwise-green tw-shadow active:tw-bg-eventwise-green/90',
    },
    'is-info': {
        default:
            'tw-bg-white tw-text-eventwise-indigo tw-border-eventwise-indigo hover:tw-bg-eventwise-indigo/10 hover:tw-border-eventwise-indigo active:tw-bg-eventwise-indigo/20',
        glass: 'tw-bg-eventwise-indigo/10 tw-text-eventwise-indigo tw-border-eventwise-indigo/40 hover:tw-bg-eventwise-indigo hover:tw-text-white hover:tw-border-eventwise-indigo active:tw-bg-eventwise-indigo/90',
        active: 'tw-bg-eventwise-indigo/10 tw-text-eventwise-indigo tw-border-eventwise-indigo tw-shadow active:tw-bg-eventwise-indigo/20',
        glassActive:
            'tw-bg-eventwise-indigo tw-text-white tw-border-eventwise-indigo tw-shadow active:tw-bg-eventwise-indigo/90',
    },
    'is-warning': {
        default:
            'tw-bg-eventwise-yellow tw-text-white tw-border-eventwise-yellow hover:tw-bg-eventwise-yellow/90 hover:tw-border-eventwise-yellow/90 active:tw-bg-eventwise-yellow/80',
        glass: 'tw-bg-eventwise-yellow/10 tw-text-eventwise-yellow tw-border-eventwise-yellow/40 hover:tw-bg-eventwise-yellow hover:tw-text-white hover:tw-border-eventwise-yellow active:tw-bg-eventwise-yellow/90',
        active: 'tw-bg-eventwise-yellow/90 tw-text-white tw-border-eventwise-yellow/90 tw-shadow active:tw-bg-eventwise-yellow/80',
        glassActive:
            'tw-bg-eventwise-yellow tw-text-white tw-border-eventwise-yellow tw-shadow active:tw-bg-eventwise-yellow/90',
    },
    'is-secondary': {
        default:
            'tw-bg-white tw-text-gray-700 tw-border-gray-300 hover:tw-bg-gray-50 hover:tw-border-gray-400 active:tw-bg-gray-100',
        glass: 'tw-bg-gray-500/10 tw-text-gray-700 tw-border-gray-400/40 hover:tw-bg-gray-700 hover:tw-text-white hover:tw-border-gray-700 active:tw-bg-gray-800',
        active: 'tw-bg-gray-50 tw-text-gray-700 tw-border-gray-400 tw-shadow active:tw-bg-gray-100',
        glassActive: 'tw-bg-gray-700 tw-text-white tw-border-gray-700 tw-shadow active:tw-bg-gray-800',
    },
    'is-xero': {
        default: 'tw-bg-xero tw-text-white/90 tw-border-none hover:tw-text-white',
        glass: 'tw-bg-xero/10 tw-text-xero tw-border-xero/40 hover:tw-bg-xero hover:tw-text-white hover:tw-border-xero active:tw-bg-xero/90',
        active: 'tw-bg-xero tw-text-white tw-border-none tw-shadow',
        glassActive: 'tw-bg-xero tw-text-white tw-border-xero tw-shadow active:tw-bg-xero/90',
    },
    'is-link': {
        default:
            'tw-bg-transparent tw-text-eventwise-link tw-border-none tw-px-0 !tw-shadow-none hover:tw-shadow-none tw-font-medium hover:tw-text-eventwise-link/90 hover:tw-underline active:tw-scale-100',
        active: 'tw-bg-transparent tw-text-eventwise-link/90 tw-border-none tw-px-0 !tw-shadow-none tw-font-medium tw-underline active:tw-scale-100',
    },
    'is-submenu': {
        default:
            'tw-rounded-none !tw-shadow-none !tw-duration-100 tw-border-0 tw-pt-0 !tw-px-2 hover:tw-border-b-[3px] active:!tw-bg-transparent tw-text-white/65 hover:!tw-text-white tw-text-[10px] tw-font-normal hover:tw-border-eventwise',
        active: 'tw-rounded-none !tw-shadow-none tw-bg-gradient-to-t tw-from-white/10 tw-to-transparent hover:tw-bg-transparent tw-border-0 tw-pt-0 !tw-px-2 tw-border-b-[3px] !tw-text-eventwise hover:!tw-text-eventwise tw-text-[10px] tw-font-normal !tw-border-eventwise hover:tw-border-white',
    },
    'is-dropdown': {
        default:
            '!tw-rounded-none tw-font-normal tw-bg-white tw-text-gray-700 tw-border-0 !tw-shadow-none hover:tw-shadow-none hover:tw-bg-gray-50 active:tw-bg-gray-100 hover:!tw-no-underline',
        active: '!tw-rounded-none tw-font-normal tw-bg-eventwise-50 tw-text-eventwise tw-border-0 !tw-shadow-none !tw-no-underline active:tw-bg-gray-100',
    },
    'is-ghost': {
        default:
            '!tw-border-0 !tw-bg-transparent !tw-shadow-none tw-text-slate-500 focus:tw-ring-1 focus:tw-ring-slate-300 hover:tw-bg-white hover:tw-text-slate-800 active:tw-bg-white/80',
        glass: 'tw-bg-transparent tw-text-white/60 tw-border-white/0 tw-shadow-none hover:tw-bg-white/10 hover:tw-text-white hover:tw-border-white/40 active:tw-bg-white/15',
        active: '!tw-border-0 !tw-bg-white !tw-shadow-none tw-text-slate-800 focus:tw-ring-1 focus:tw-ring-slate-300 active:tw-bg-white/80',
        glassActive: 'tw-bg-white/10 tw-text-white tw-border-white/40 tw-shadow-none active:tw-bg-white/15',
    },
    default: {
        default:
            'tw-bg-white tw-shadow-none tw-border-gray-300 tw-text-gray-700 hover:tw-bg-gray-50 hover:tw-border-gray-400 active:tw-bg-gray-100',
        glass: 'tw-bg-transparent tw-text-white/60 tw-border-white/25 tw-shadow-none hover:tw-bg-white/10 hover:tw-text-white hover:tw-border-white/40 active:tw-bg-white/15',
        active: 'tw-bg-gray-50 tw-shadow tw-border-gray-400 tw-text-gray-700 active:tw-bg-gray-100',
        glassActive: 'tw-bg-white/10 tw-text-white tw-border-white/40 tw-shadow active:tw-bg-white/15',
    },
};

const classes = computed(() => {
    const styleKey = props.active && !props.type ? 'is-primary' : (props.type ?? 'default');
    const style = buttonStyles[styleKey];
    const activeStyle = props.glass ? (style.glassActive ?? style.active) : style.active;

    return [
        props.active && activeStyle ? activeStyle : props.glass && style.glass ? style.glass : style.default,
        props.disabled ? 'tw-opacity-50 tw-cursor-not-allowed' : '',
        !props.compact ? 'tw-px-3.5 tw-py-1.5' : '',
        props.compact ? 'tw-py-1 tw-px-2.5 tw-text-xs' : '',
        props.square ? '!tw-px-2' : '',
    ];
});
</script>
<style lang="scss" scoped>
button {
    & > i,
    & > div > i {
        display: block;
        transform: scale(1.33);
    }
}
</style>
